import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useSEO } from "@/hooks/useSEO";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { CATEGORY_OPTIONS, FORMAT_OPTIONS, STAGE_OPTIONS } from "@/lib/data";
import { Upload, Check, ArrowRight, Loader2, FileText, X } from "lucide-react";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="label text-ws-dark block mb-2.5">{label}{required && <span className="text-ws-accent"> *</span>}</span>
    {children}
  </label>
);

const inputCls = "w-full min-w-0 max-w-full bg-ws-card border border-ws-border rounded-lg px-3 sm:px-4 py-3.5 text-ws-dark text-base placeholder:text-ws-text2/60 focus:outline-none focus:border-ws-accent focus:ring-2 focus:ring-ws-accent/20 transition-colors";

export default function RequestQuote() {
  useSEO("Request a Quote | Wisperwick Studio",
    "Tell Wisperwick Studio what you're building. Request a manufacturing quote for beverage mixes, hydration products, energy stick packs and functional beverages.");

  const empty = {
    full_name: "", company_name: "", business_email: "", phone: "", country: "",
    product_category: "", product_idea: "", formulation_support: "", desired_format: "",
    estimated_quantity: "", target_market: "", target_launch_date: "", current_stage: "", message: "",
  };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.full_name || !form.business_email) {
      toast.error("Please fill in your name and business email.");
      return;
    }
    setSubmitting(true);
    try {
      // Submit to Netlify Forms as multipart/form-data (supports file uploads).
      // The matching hidden <form name="quote"> in public/index.html lets
      // Netlify's build bot detect and capture these submissions after deploy.
      const fd = new FormData();
      fd.append("form-name", "quote");
      fd.append("bot-field", "");
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("product_brief", file);

      await fetch("/", { method: "POST", body: fd });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-5 sm:px-8 lg:px-14 pt-28 sm:pt-36 lg:pt-44 pb-16 sm:pb-24">
      <div className="max-w-[1100px] mx-auto">
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div key="thanks" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="min-h-[50vh] flex flex-col items-center justify-center text-center" data-testid="quote-success">
              <div className="w-16 h-16 rounded-full bg-ws-accent flex items-center justify-center mb-8">
                <Check className="w-8 h-8 text-white" strokeWidth={2.5} />
              </div>
              <h1 className="font-display text-4xl sm:text-5xl text-ws-dark max-w-2xl text-balance">Thank you — we've received your project enquiry.</h1>
              <p className="text-ws-text2 mt-6 max-w-lg">Our team will review your requirements and get back to you.</p>
              <button onClick={() => { setForm(empty); setFile(null); setDone(false); }} data-testid="submit-another"
                className="mt-10 label inline-flex items-center gap-2 border border-ws-dark rounded-full px-7 py-3.5 text-ws-dark hover:bg-ws-dark hover:text-ws-bg transition">
                Submit Another Enquiry <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Reveal><Eyebrow>Request a Quote</Eyebrow></Reveal>
              <Reveal delay={0.05}><h1 data-testid="quote-title" className="display-hero mt-7 text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-ws-dark">Have an idea? Let's talk.</h1></Reveal>
              <Reveal delay={0.1}><p className="mt-6 text-lg text-ws-text2 max-w-2xl">Tell us what you're building and where you want to take it.</p></Reveal>

              <Reveal delay={0.15}>
                <form onSubmit={(e) => e.preventDefault()} name="quote" method="POST" data-netlify="true" netlify-honeypot="bot-field" encType="multipart/form-data" className="mt-10 sm:mt-14 bg-ws-bg2 border border-ws-border rounded-2xl sm:rounded-3xl p-4 sm:p-8 lg:p-10" data-testid="quote-form">
                  <input type="hidden" name="form-name" value="quote" />
                  <p className="hidden"><label>Don't fill this out: <input name="bot-field" /></label></p>
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field label="Full Name" required><input data-testid="input-full-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" /></Field>
                    <Field label="Company Name"><input data-testid="input-company" className={inputCls} value={form.company_name} onChange={set("company_name")} placeholder="Company (optional)" /></Field>
                    <Field label="Business Email" required><input data-testid="input-email" type="email" className={inputCls} value={form.business_email} onChange={set("business_email")} placeholder="you@company.com" /></Field>
                    <Field label="Phone"><input data-testid="input-phone" type="tel" autoComplete="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Phone number" /></Field>
                    <Field label="Country"><input data-testid="input-country" className={inputCls} value={form.country} onChange={set("country")} placeholder="Country" /></Field>
                    <Field label="Product Category">
                      <select data-testid="select-category" className={inputCls} value={form.product_category} onChange={set("product_category")}>
                        <option value="">Select category</option>
                        {CATEGORY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Desired Format">
                      <select data-testid="select-format" className={inputCls} value={form.desired_format} onChange={set("desired_format")}>
                        <option value="">Select format</option>
                        {FORMAT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Current Stage">
                      <select data-testid="select-stage" className={inputCls} value={form.current_stage} onChange={set("current_stage")}>
                        <option value="">Select stage</option>
                        {STAGE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field label="Estimated Quantity"><input data-testid="input-quantity" className={inputCls} value={form.estimated_quantity} onChange={set("estimated_quantity")} placeholder="e.g. 10,000 units" /></Field>
                    <Field label="Target Market"><input data-testid="input-market" className={inputCls} value={form.target_market} onChange={set("target_market")} placeholder="e.g. DTC / retail / region" /></Field>
                    <Field label="Target Launch Date"><input data-testid="input-launch" className={inputCls} value={form.target_launch_date} onChange={set("target_launch_date")} placeholder="e.g. Q4 2026" /></Field>
                    <Field label="Do you need formulation support?">
                      <div className="grid grid-cols-3 gap-2">
                        {["Yes", "No", "Not sure"].map((v) => (
                          <button key={v} type="button" data-testid={`formulation-${v.toLowerCase().replace(" ", "-")}`}
                            aria-pressed={form.formulation_support === v} onClick={() => setForm({ ...form, formulation_support: v })}
                            className={`min-h-12 label text-[0.65rem] sm:text-[0.72rem] tracking-[0.08em] rounded-lg px-1 py-3.5 border transition-colors ${form.formulation_support === v ? "bg-ws-dark text-ws-bg border-ws-dark" : "bg-ws-card text-ws-dark border-ws-border hover:border-ws-accent"}`}>
                            {v}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>

                  <div className="mt-5">
                    <Field label="Product Idea"><textarea data-testid="input-idea" rows={3} className={inputCls} value={form.product_idea} onChange={set("product_idea")} placeholder="Describe the product you want to build..." /></Field>
                  </div>
                  <div className="mt-5">
                    <Field label="Message"><textarea data-testid="input-message" rows={3} className={inputCls} value={form.message} onChange={set("message")} placeholder="Anything else we should know?" /></Field>
                  </div>

                  {/* File upload */}
                  <div className="mt-5">
                    <span className="label text-ws-dark block mb-2.5">Upload Formulation / Product Brief</span>
                    <input ref={fileRef} type="file" data-testid="input-file" className="hidden"
                      accept=".pdf,.doc,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => setFile(e.target.files[0] || null)} />
                    {!file ? (
                      <button type="button" onClick={() => fileRef.current?.click()} data-testid="upload-trigger"
                        className="w-full border border-dashed border-ws-border rounded-lg py-8 flex flex-col items-center gap-2 bg-ws-card hover:border-ws-accent transition">
                        <Upload className="w-5 h-5 text-ws-accent" />
                        <span className="text-sm text-ws-text2">Click to upload (PDF, DOC, image · max 15MB)</span>
                      </button>
                    ) : (
                      <div className="w-full border border-ws-border rounded-lg pl-4 pr-2 py-2 flex items-center gap-2 bg-ws-card" data-testid="file-selected">
                        <span className="min-w-0 flex-1 flex items-center gap-3 text-sm text-ws-dark"><FileText className="w-4 h-4 text-ws-accent shrink-0" /><span data-testid="selected-file-name" className="truncate" title={file.name}>{file.name}</span></span>
                        <button type="button" data-testid="remove-file" aria-label="Remove uploaded brief" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="min-h-11 min-w-11 flex items-center justify-center text-ws-text2 hover:text-ws-dark shrink-0"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>

                  <button type="button" onClick={submit} disabled={submitting} data-testid="submit-quote"
                    className="mt-8 w-full sm:w-auto group inline-flex items-center justify-center gap-2 label bg-ws-dark text-ws-bg rounded-full px-10 py-4 hover:bg-ws-accentHover transition disabled:opacity-60">
                    {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <>Request a Quote <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>}
                  </button>
                </form>
              </Reveal>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
